import { useState, useEffect } from "react";
import { Menu, X, Zap, LogOut, Sun, Moon, User, ChevronDown, Settings, Trophy, Swords } from "lucide-react";
import PremiumNotificationBell from "@/components/PremiumNotificationBell";
import logoKelasAI from "@/assets/logo-kelasai.webp";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAuthModal } from "@/hooks/useAuthModal";
import { useTheme } from "next-themes";
import { getUserAccessContext } from "@/lib/access";
import AppSettingsModal from "@/components/AppSettingsModal";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const Navbar = () => {
  const { user, signOut } = useAuth();
  const { openLogin, openSignup } = useAuthModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [adminRole, setAdminRole] = useState<"admin" | "super_admin" | null>(null);
  const { theme, setTheme } = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string>("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingChallengeInvites, setPendingChallengeInvites] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!user) { setUserRole(null); setAdminRole(null); setAvatarUrl(null); setFullName(""); return; }

    getUserAccessContext(user.id).then((access) => {
      setUserRole(access.profileRole);
      setAdminRole(access.adminRole);
    });

    supabase.from("profiles").select("avatar_url, full_name").eq("id", user.id).single().then(({ data }) => {
      if (data) {
        setAvatarUrl(data.avatar_url);
        setFullName(data.full_name || "");
      }
    });

    supabase.from("challenge_invites").select("id").eq("to_user_id", user.id).eq("status", "pending").then(({ data }) => {
      setPendingChallengeInvites(data?.length || 0);
    });

    const playNotificationSound = () => {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.value = 0.18;
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.4);
      } catch {}
    };

    const channel = supabase
      .channel("navbar-invites")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "challenge_invites", filter: `to_user_id=eq.${user.id}` }, async (payload: any) => {
        playNotificationSound();
        supabase.from("challenge_invites").select("id").eq("to_user_id", user.id).eq("status", "pending").then(({ data }) => {
          setPendingChallengeInvites(data?.length || 0);
        });

        // Show toast with sender info
        const fromUserId = payload.new?.from_user_id;
        const subject = payload.new?.subject || "un défi";
        let senderName = "Un adversaire";
        if (fromUserId) {
          const { data: prof } = await supabase.from("profiles").select("full_name").eq("id", fromUserId).single();
          if (prof?.full_name) senderName = prof.full_name;
        }
        toast(`⚔️ ${senderName} t'invite à un défi !`, {
          description: `Matière : ${subject}. Clique pour rejoindre.`,
          duration: 8000,
          action: {
            label: "Voir",
            onClick: () => navigate("/challenges"),
          },
        });
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "challenge_invites", filter: `to_user_id=eq.${user.id}` }, () => {
        supabase.from("challenge_invites").select("id").eq("to_user_id", user.id).eq("status", "pending").then(({ data }) => {
          setPendingChallengeInvites(data?.length || 0);
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, navigate]);

  // Realtime: refresh navbar avatar/name when profile is updated elsewhere
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`navbar-profile-${user.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${user.id}` },
        (payload: any) => {
          const row = payload.new;
          if (!row) return;
          setAvatarUrl(row.avatar_url ?? null);
          setFullName(row.full_name || "");
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = () => setDropdownOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [dropdownOpen]);

  const isAdmin = Boolean(adminRole);
  const dashboardPath = adminRole === "super_admin" ? "/admin" : userRole === "parent" ? "/parent-dashboard" : "/dashboard";

  const navLinks = [
    { href: "/", label: t("nav.home", "Accueil") },
    { href: "/fonctionnalites", label: t("nav.features") },
    { href: "/gamification", label: t("nav.gamification") },
    { href: "/tarifs", label: t("nav.pricing") },
  ];

  const studentExtraLinks: { href: string; label: string }[] = [];

  const handleSignOut = async () => {
    await signOut();
    window.location.replace("/");
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  const isEmoji = avatarUrl && avatarUrl.length <= 4 && !avatarUrl.startsWith("http");

  const avatarElement = (
    <div className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden border-2 border-primary/20" style={{ background: "hsl(var(--muted))" }}>
      {isEmoji ? (
        <span className="text-lg">{avatarUrl}</span>
      ) : avatarUrl ? (
        <img src={avatarUrl} alt="Avatar" data-avatar-preview="true" className="w-full h-full object-cover cursor-zoom-in" />
      ) : (
        <User className="w-4 h-4 text-muted-foreground" />
      )}
    </div>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 nav-scrolled">
      <div className="container mx-auto px-3 sm:px-6 flex items-center justify-between gap-2 py-2">
        <Link to="/" className="flex items-center gap-1.5 group shrink-0">
          <img src={logoKelasAI} alt="KelasAI" className="h-12 md:h-[80px] w-auto transition-transform duration-300 group-hover:scale-110" />
          <span className="hidden sm:inline ml-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-primary/15 text-primary border border-primary/25">Beta</span>
        </Link>

        {/* Mobile horizontal tabs */}
        <div className="md:hidden flex-1 flex items-center justify-start gap-1 overflow-x-auto no-scrollbar touch-pan-x" style={{ WebkitOverflowScrolling: "touch" }}>
          {navLinks.map((link) => {
            const active = link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`shrink-0 text-[9px] font-semibold transition-colors px-1.5 py-1 rounded-lg ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:text-primary"}`}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="shrink-0 p-1 rounded-lg text-foreground/70 hover:text-primary hover:bg-muted transition-colors"
          >
            {theme === "dark" ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
          </button>
          {!user && (
            <>
              <button
                onClick={openLogin}
                className="shrink-0 text-[9px] font-semibold px-1.5 py-1 rounded-lg text-foreground/70 hover:text-primary transition-colors"
              >
                {t("nav.login")}
              </button>
              <button
                onClick={openSignup}
                className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-2 py-1 rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                <Zap className="w-3 h-3" /> {t("nav.signup")}
              </button>
            </>
          )}
          {user && (
            <button
              onClick={handleSignOut}
              className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-1 rounded-lg text-destructive hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="w-3 h-3" /> {t("nav.logout")}
            </button>
          )}
        </div>

        <div className="hidden lg:flex items-center gap-7">
          {navLinks.map((link) => {
            const active = link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-semibold transition-colors duration-200 px-3 py-1.5 rounded-xl ${active ? "bg-primary/10 text-primary" : "text-foreground/65 hover:text-primary"}`}
              >
                {link.label}
              </Link>
            );
          })}
          {studentExtraLinks.map((link) => (
            <Link key={link.label} to={link.href} className="text-sm font-semibold text-foreground/65 hover:text-primary transition-colors duration-200">
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2 lg:gap-3">
          <PremiumNotificationBell />
          <button onClick={toggleTheme} className="p-2.5 rounded-2xl text-foreground/70 hover:text-primary hover:bg-muted transition-colors" aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <div className="relative">
              <button
                onClick={(e) => { e.stopPropagation(); setDropdownOpen(!dropdownOpen); }}
                className="flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-2xl hover:bg-muted transition-colors relative"
              >
                {avatarElement}
                {pendingChallengeInvites > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center animate-pulse">
                    {pendingChallengeInvites}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <span className="text-xs font-bold text-foreground max-w-[120px] truncate">{fullName || t("nav.myProfile")}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-card border border-border rounded-2xl shadow-lg py-2 z-50" onClick={(e) => e.stopPropagation()}>
                  <Link to={dashboardPath} onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                    📊 {t("nav.dashboard")}
                  </Link>
                  <Link to="/challenges" onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors relative">
                    <Trophy className="w-4 h-4" /> {t("nav.challenges")}
                    {pendingChallengeInvites > 0 && (
                      <span className="ml-auto w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center">
                        {pendingChallengeInvites}
                      </span>
                    )}
                  </Link>
                  <div className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                    <PremiumNotificationBell />
                    <span>{t("nav.notifications", "Notifications")}</span>
                  </div>
                  {isAdmin && (
                    <Link to={adminRole === "super_admin" ? "/admin" : "/admin/users"} onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                      🛡️ {adminRole === "super_admin" ? t("nav.adminConsole") : t("nav.admin")}
                    </Link>
                  )}
                  {userRole === "enseignant" && adminRole !== "super_admin" && (
                    <Link to="/teacher-dashboard" onClick={() => setDropdownOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                      👨‍🏫 Espace enseignant
                    </Link>
                  )}
                  <button onClick={() => { setSettingsOpen(true); setDropdownOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                    <Settings className="w-4 h-4" /> {t("nav.settings")}
                  </button>
                  <hr className="my-1 border-border" />
                  <button onClick={() => { handleSignOut(); setDropdownOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-muted transition-colors">
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <button onClick={openLogin} className="px-5 py-2.5 rounded-2xl text-sm font-bold text-foreground/70 hover:text-primary transition-colors">{t("nav.login")}</button>
              <button onClick={openSignup} className="btn-hero text-sm py-2.5 px-5">
                <Zap className="w-4 h-4" /> {t("nav.signup")}
              </button>
            </>
          )}
        </div>

        <button aria-label={isMobileOpen ? "Fermer le menu" : "Ouvrir le menu"} aria-expanded={isMobileOpen} className="hidden shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors" onClick={() => setIsMobileOpen(!isMobileOpen)}>
          {isMobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-card/98 backdrop-blur-xl border-t border-border px-6 py-4 flex flex-col gap-3">
          {navLinks.map((link) => {
            const active = link.href === "/" ? location.pathname === "/" : location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setIsMobileOpen(false)}
                className={`py-2 px-3 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:text-primary"}`}
              >
                {link.label}
              </Link>
            );
          })}
          <button onClick={toggleTheme} className="py-2 text-sm font-semibold text-foreground/70 hover:text-primary transition-colors flex items-center gap-2">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {theme === "dark" ? t("nav.lightMode") : t("nav.darkMode")}
          </button>
          <hr className="border-border my-1" />
          {user ? (
            <>
              <div className="flex items-center gap-3 py-2">
                {avatarElement}
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-foreground">{fullName || t("nav.myProfile")}</span>
                  {adminRole === "super_admin" ? (
                    <span className="text-xs text-muted-foreground">Super administrateur</span>
                  ) : adminRole === "admin" ? (
                    <span className="text-xs text-muted-foreground">Administrateur</span>
                  ) : userRole === "enseignant" ? (
                    <span className="text-xs text-muted-foreground">Enseignant</span>
                  ) : null}
                </div>
              </div>
              <hr className="border-border my-1" />
              <Link to={dashboardPath} onClick={() => setIsMobileOpen(false)} className="py-2 px-3 rounded-xl text-sm font-bold text-foreground/70 hover:text-primary hover:bg-muted transition-colors flex items-center gap-2">
                📊 {t("nav.dashboard")}
              </Link>
              <Link to="/challenges" onClick={() => setIsMobileOpen(false)} className="py-2 px-3 rounded-xl text-sm font-bold text-foreground/70 hover:text-primary hover:bg-muted transition-colors flex items-center gap-2 relative">
                <Trophy className="w-4 h-4" /> {t("nav.challenges")}
                {pendingChallengeInvites > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-black flex items-center justify-center">
                    {pendingChallengeInvites}
                  </span>
                )}
              </Link>
              {isAdmin && (
                <Link to={adminRole === "super_admin" ? "/admin" : "/admin/users"} onClick={() => setIsMobileOpen(false)} className="py-2 px-3 rounded-xl text-sm font-bold text-foreground/70 hover:text-primary hover:bg-muted transition-colors flex items-center gap-2">
                  🛡️ {adminRole === "super_admin" ? t("nav.adminConsole") : t("nav.admin")}
                </Link>
              )}
              {userRole === "enseignant" && adminRole !== "super_admin" && (
                <Link to="/teacher-dashboard" onClick={() => setIsMobileOpen(false)} className="py-2 px-3 rounded-xl text-sm font-bold text-foreground/70 hover:text-primary hover:bg-muted transition-colors flex items-center gap-2">
                  👨‍🏫 Espace enseignant
                </Link>
              )}
              <button onClick={() => { setSettingsOpen(true); setIsMobileOpen(false); }} className="w-full text-left py-2 px-3 rounded-xl text-sm font-bold text-foreground/70 hover:text-primary hover:bg-muted transition-colors flex items-center gap-2">
                <Settings className="w-4 h-4" /> {t("nav.settings")}
              </button>
              <hr className="border-border my-1" />
              <button onClick={() => { handleSignOut(); setIsMobileOpen(false); }} className="w-full text-left py-2 px-3 rounded-xl text-sm font-bold text-destructive hover:bg-muted transition-colors flex items-center gap-2">
                <LogOut className="w-4 h-4" /> {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { openLogin(); setIsMobileOpen(false); }} className="py-2 text-sm font-bold text-foreground/70 text-center">{t("nav.login")}</button>
              <button onClick={() => { openSignup(); setIsMobileOpen(false); }} className="btn-hero text-sm py-3 justify-center">
                <Zap className="w-4 h-4" /> {t("nav.signup")}
              </button>
            </>
          )}
        </div>
      )}
      <AppSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </nav>
  );
};

export default Navbar;
